import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, FilePlus, UserPlus, Building2, Clock, CheckCircle, FileText, Users, ChevronRight, Menu, X, Bell } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import '../Dashboard/Dashboard.css';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const result = await api.notifications.unreadCount();
      setUnreadCount(result?.count || result?.data?.count || 0);
    } catch (err) {
      console.error('Failed to fetch notification count:', err);
    }
  };

  const fetchNotifications = async () => {
    try {
      const result = await api.notifications.list();
      const items = result?.data?.notifications || result?.notifications || result || [];
      setNotifications(Array.isArray(items) ? items.slice(0, 10) : []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const handleBellClick = () => {
    if (!showNotifications) fetchNotifications();
    setShowNotifications(!showNotifications);
  };

  const handleNotificationClick = async (notif) => {
    try {
      if (!notif.read_at) {
        await api.notifications.markRead(notif.id);
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {}
    setShowNotifications(false);
    if (notif.case_id) navigate(`/cases/${notif.case_id}`);
  };

  const handleMarkAllRead = async () => {
    try {
      await api.notifications.markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
    } catch (err) {}
  };

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { id: 'divider1', divider: true },
    { id: 'create-case', label: 'Create Case', icon: FilePlus, path: '/cases/new' },
    { id: 'create-user', label: 'Create User', icon: UserPlus, path: '/users' },
    { id: 'create-university', label: 'Create University', icon: Building2, path: '/universities' },
    { id: 'divider2', divider: true },
    { id: 'pending', label: 'Pending Cases', icon: Clock, path: '/cases?status=pending' },
    { id: 'completed', label: 'Completed Cases', icon: CheckCircle, path: '/cases?status=completed' },
    { id: 'all-cases', label: 'All Cases', icon: FileText, path: '/cases' },
    { id: 'divider3', divider: true },
    { id: 'manage-apply-for', label: 'Manage Apply For', icon: FileText, path: '/manage-apply-for' },
    { id: 'users', label: 'All Users', icon: Users, path: '/users' },
  ];

  const isActivePath = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    return location.pathname + location.search === path || location.pathname === path;
  };

  const handleSidebarClick = (path) => { setSidebarOpen(false); navigate(path); };
  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className={`admin-layout ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <LayoutDashboard size={24} />
            <span>Admin Panel</span>
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}><X size={20} /></button>
        </div>
        <nav className="sidebar-nav">
          {sidebarItems.map((item) => item.divider ? (
            <div key={item.id} className="sidebar-divider" />
          ) : (
            <button key={item.id} className={`sidebar-item ${isActivePath(item.path) ? 'active' : ''}`} onClick={() => handleSidebarClick(item.path)}>
              <item.icon size={18} /><span>{item.label}</span><ChevronRight size={14} className="sidebar-arrow" />
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{user?.name?.charAt(0) || 'A'}</div>
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{user?.name || 'Admin'}</span>
              <span className="sidebar-user-email">{user?.email || ''}</span>
            </div>
          </div>
          <button className="sidebar-logout" onClick={handleLogout}>Logout</button>
        </div>
      </aside>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <main className="admin-main">
        <div className="admin-topbar">
          <div className="admin-topbar-left">
            <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginLeft: 'auto', paddingRight: '16px' }}>
            <div style={{ position: 'relative' }}>
              <button onClick={handleBellClick} style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Notifications">
                <Bell size={22} style={{ color: '#64748b' }} />
                {unreadCount > 0 && (
                  <span style={{ position: 'absolute', top: '2px', right: '2px', backgroundColor: '#ef4444', color: 'white', fontSize: '10px', fontWeight: 700, minWidth: '18px', height: '18px', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1 }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', width: '360px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', zIndex: 1000, maxHeight: '400px', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ fontWeight: 600, fontSize: '15px' }}>Notifications</span>
                    {unreadCount > 0 && <button onClick={handleMarkAllRead} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>Mark all read</button>}
                  </div>
                  <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '30px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>No notifications yet</div>
                    ) : (
                      notifications.map((notif) => (
                        <div key={notif.id} onClick={() => handleNotificationClick(notif)} style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', backgroundColor: notif.read_at ? 'white' : '#f0f7ff', transition: 'background 0.15s' }}>
                          <div style={{ fontSize: '13px', fontWeight: notif.read_at ? 400 : 600, color: '#1e293b', marginBottom: '4px' }}>{notif.title || notif.message || 'New notification'}</div>
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>{notif.created_at ? new Date(notif.created_at).toLocaleString() : ''}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
